import type { User, UserManager } from "oidc-client-ts";

import { OIDC_STORAGE_PREFIX } from "./oidcConfig";

export type AuthSignalType = "login" | "logout";

export type AuthSignal = Readonly<{
  type: AuthSignalType;
  at: number;
}>;

export const AUTH_SIGNAL_KEY = "onecap.auth:sync-signal:v1";

export function getOidcUserStorageKey(authority: string, clientId: string): string {
  // This matches oidc-client-ts internal key format.
  return `${OIDC_STORAGE_PREFIX}oidc.user:${authority}:${clientId}`;
}

export function emitAuthSignal(storage: Storage, signal: AuthSignal): void {
  storage.setItem(AUTH_SIGNAL_KEY, JSON.stringify(signal));
}

export type BindOidcEventsParams = Readonly<{
  userManager: UserManager;
  onUserLoaded: (user: User) => void;
  onUserUnloaded: () => void;
  onSilentRenewError: (error: unknown) => void;
}>;

export function bindOidcEvents(params: BindOidcEventsParams): () => void {
  const { userManager, onUserLoaded, onUserUnloaded, onSilentRenewError } = params;

  const handleUserLoaded = (user: User) => onUserLoaded(user);
  const handleUserUnloaded = () => onUserUnloaded();
  const handleSilentRenewError = (error: unknown) => onSilentRenewError(error);

  userManager.events.addUserLoaded(handleUserLoaded);
  userManager.events.addUserUnloaded(handleUserUnloaded);
  userManager.events.addSilentRenewError(handleSilentRenewError);

  return () => {
    userManager.events.removeUserLoaded(handleUserLoaded);
    userManager.events.removeUserUnloaded(handleUserUnloaded);
    userManager.events.removeSilentRenewError(handleSilentRenewError);
  };
}

export type BindCrossTabSyncParams = Readonly<{
  userManager: UserManager;
  authority: string;
  clientId: string;
  onRemoteUserUpdated: (user: User | null) => void;
  onRemoteLogout: () => void;
  // optional error reporting (kept to console.error-only requirement)
  onError?: (error: unknown) => void;
}>;

export function bindCrossTabSync(params: BindCrossTabSyncParams): () => void {
  const { userManager, authority, clientId, onRemoteUserUpdated, onRemoteLogout, onError } =
    params;

  const userKey = getOidcUserStorageKey(authority, clientId);

  const handler = (event: StorageEvent) => {
    try {
      if (!event.key) return;

      if (event.key === userKey) {
        // A different tab updated/cleared the OIDC user record; refresh our view.
        void userManager.getUser().then((user) => onRemoteUserUpdated(user));
        return;
      }

      if (event.key === AUTH_SIGNAL_KEY && typeof event.newValue === "string") {
        const parsed = JSON.parse(event.newValue) as Partial<AuthSignal> | null;
        if (parsed?.type === "logout") {
          onRemoteLogout();
          return;
        }
        if (parsed?.type === "login") {
          void userManager.getUser().then((user) => onRemoteUserUpdated(user));
        }
      }
    } catch (error) {
      onError?.(error);
    }
  };

  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}

